/**
 * Providers — composes the app-wide context providers (router + toasts + branding).
 *
 * Kept separate from the shell so tests can mount pages with the same context stack.
 */
import type { ReactNode } from "react";

import { BrandingProvider } from "@melusine/shared-ui";
import { useMe } from "@entities/user";
import { CartProvider } from "@features/cart";
import { RouterProvider } from "@shared/router";
import { ToastProvider } from "@shared/ui/toast";

export interface ProvidersProps {
  readonly children: ReactNode;
}

/**
 * BrandingGate — feeds the brand asset URLs from ``GET /api/me`` (object storage)
 * into the shared-ui {@link BrandingProvider}. Before ``me`` loads (or if it fails)
 * branding stays empty and components render their built-in fallback glyphs.
 */
function BrandingGate({ children }: { readonly children: ReactNode }): JSX.Element {
  const { data: me } = useMe();
  return (
    <BrandingProvider value={me?.branding ?? {}}>{children}</BrandingProvider>
  );
}

export function Providers({ children }: ProvidersProps): JSX.Element {
  return (
    <RouterProvider>
      <ToastProvider>
        <BrandingGate>
          <CartProvider>{children}</CartProvider>
        </BrandingGate>
      </ToastProvider>
    </RouterProvider>
  );
}
